import React, { type FC, type ReactElement, useEffect, useState } from 'react'
import {
  Button,
  Checkbox,
  FormControlLabel,
  Input,
  MenuItem,
  type SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { GridContainer, StyledChip, StyledHeaderCol, StyledRow, StyledSelect, StyledTextField } from './Settings.styles'
import { useTranslation } from 'react-i18next'
import {
  AddEmailRecipientAction,
  AddSMSRecipientAction,
  ChangeEmailIntervalAction,
  ChangeSMSIntervalAction,
  FetchCustomerAlarmsAction,
  RemoveEmailRecipientAction,
  RemoveSMSRecipientAction,
  SaveCustomerAlarmsAction,
  SetEmailEnabledAction,
  SetSMSEnabledAction,
  SetTemperatureAlertsEnabledAction,
  UpdateSMSRecipientTextBoxAction,
  UpdateTemperatureAlertLowerLimitAction,
  UpdateTemperatureAlertUpperLimitAction
} from '../../../redux/settings/setting-actions'
import { type AlarmSettings } from '../../../redux/settings/settings-type'
import { type RootState } from '../../../redux/store'
import { useAppDispatch, useAppSelector } from '../../../redux/hooks'
import { type OwnCompany } from '../../../redux/auth/auth-types'

/**
 * Settings component.
 */
export const Settings: FC = () => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const ownCompany = useAppSelector<OwnCompany | undefined>((state: RootState) => state.auth.ownCompany ?? undefined)
  const ownCompanyId = ownCompany?.id

  // Load alarm settings when own company changes
  useEffect(() => {
    if (ownCompanyId != null) {
      // note: this action will update 'alarmSettingsCompanyId' to 'ownCompanyId'
      dispatch(FetchCustomerAlarmsAction({ id: ownCompanyId }))
    }
  }, [dispatch, ownCompanyId])

  return (
        <div style={{ marginLeft: '20%', marginRight: '20%' }}>
            <Table>
                <TableHead>
                    <TableRow>
                        <StyledHeaderCol gray={false}>
                            <h3>{t('settings_o.settings')}</h3>
                        </StyledHeaderCol>
                    </TableRow>
                </TableHead>
                <TableBody>
                    <SettingsHeader/>
                    <EmailSettings/>
                    <SmsSettings/>
                    <TemperatureAlarmSettings/>
                    <SettingsActionButtons/>
                </TableBody>
            </Table>
        </div>
  )
}

/* Child Components */

function SettingsHeader(): ReactElement {
  const { t } = useTranslation()

  return (
        <StyledRow>
            <StyledHeaderCol gray={false}><h4>{t('settings_o.message_settings')}</h4></StyledHeaderCol>
            <StyledHeaderCol colSpan={2} gray={true}><p>{t('settings_o.recipients')}</p></StyledHeaderCol>
            <StyledHeaderCol gray={true}><p>{t('settings_o.sending_interval')}</p></StyledHeaderCol>
            <StyledHeaderCol gray={false}/>
        </StyledRow>
  )
}

function SettingsActionButtons(): ReactElement {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const alarmSettingsCompanyId = useAppSelector<string | null>((state: RootState) => state.settings.alarmSettingsCustomer)
  const alarmSettingsDirty = useAppSelector<boolean>((state: RootState) => state.settings.alarmSettingsDirty)
  const alarmSettings = useAppSelector<AlarmSettings>((state: RootState) => state.settings.alarmSettings)
  const ownCompanyId = useAppSelector<string | undefined>((state: RootState) => state.auth.ownCompany?.id ?? undefined)

  const handleCancel = (): void => {
    if (ownCompanyId != null) {
      dispatch(FetchCustomerAlarmsAction({ id: ownCompanyId }))
    } else {
      console.warn('own company ID is missing')
    }
  }

  const onSave = (): void => {
    if (alarmSettingsCompanyId == null) {
      console.warn("skipping save due to missing 'alarmSettingsCustomer' (companyId)")
      return
    }

    dispatch(SaveCustomerAlarmsAction({
      id: alarmSettingsCompanyId,
      data: alarmSettings
    }))
  }

  return (
        <StyledRow>
            <TableCell/>
            <TableCell/>
            <TableCell/>
            <GridContainer>
                <Button
                    fullWidth
                    variant={'contained'}
                    color="inherit"
                    disabled={!alarmSettingsDirty}
                    onClick={handleCancel}
                >
                    {t('settings_o.cancel')}
                </Button>
                <Button
                    fullWidth
                    color="primary"
                    variant={'contained'}
                    onClick={onSave}
                    disabled={!alarmSettingsDirty}
                >
                    {t('settings_o.save')}
                </Button>
            </GridContainer>
        </StyledRow>
  )
}

function EmailSettings(): ReactElement {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const alarmSettings = useAppSelector<AlarmSettings>((state: RootState) => state.settings.alarmSettings)
  const alarmSettingsCompanyId = useAppSelector<string | null>((state: RootState) => state.settings.alarmSettingsCustomer)

  const emailRecipients = useAppSelector<string[]>((state: RootState) => state.settings.alarmSettings.emailRecipients)
  const ownCompany = useAppSelector<OwnCompany | undefined>((state: RootState) => state.auth.ownCompany ?? undefined)

  const [emailEnabled, setEmailEnabled] = useState<boolean>(false)

  const users = ownCompany != null ? ownCompany.users : []
  const filteredUsers = users.filter(u => !emailRecipients.includes(u.username)) ?? []

  useEffect(() => {
    setEmailEnabled(alarmSettings.emailEnabled)
  }, [alarmSettings])

  const setEnableEmailSetting = (enabled: boolean): void => {
    setEmailEnabled(enabled)
    dispatch(SetEmailEnabledAction({ enabled }))
  }

  const onEmailRecipientSelected = (username: string): void => {
    dispatch(AddEmailRecipientAction(username))
  }

  return (
        <StyledRow>
            <StyledHeaderCol gray={false}>
                <p>{t('settings_o.email')}</p>
            </StyledHeaderCol>
            <TableCell colSpan={2}>
                <StyledSelect
                    value={''}
                    disableUnderline
                    input={<Input id="selectEmailRecipients"/>}
                    disabled={alarmSettingsCompanyId == null || !emailEnabled}
                    onChange={(e: SelectChangeEvent<unknown>) => {
                      onEmailRecipientSelected(e.target.value as string)
                    }}
                    IconComponent={({ className }) => <ExpandMoreIcon className={className}/>}
                >
                    {filteredUsers.map(user => {
                      return (
                            <MenuItem
                                key={'usr-select-email-' + user.username}
                                value={user.username}
                            >
                                {user.username}
                            </MenuItem>
                      )
                    })}
                </StyledSelect>
                <EmailRecipientChips/>
            </TableCell>
            <TableCell>
                <StyledSelect
                    disableUnderline
                    input={<Input id="selectEmailInverval"/>}
                    value={alarmSettings.emailInterval}
                    disabled={alarmSettingsCompanyId == null || !emailEnabled}
                    onChange={(e) => dispatch(ChangeEmailIntervalAction(parseInt(e.target.value as string)))}
                    IconComponent={({ className }) => <ExpandMoreIcon className={className}/>}
                >
                    <MenuItem value={1}>
                        {t('settings_o.daily')}
                    </MenuItem>
                    <MenuItem value={2}>
                        {t('settings_o.weekly')}
                    </MenuItem>
                </StyledSelect>
            </TableCell>
            <TableCell>
                <FormControlLabel
                    label={alarmSettings.emailEnabled ? t('settings_o.enabled') : t('settings_o.disabled')}
                    control={
                        <Checkbox
                            disabled={alarmSettingsCompanyId == null}
                            checked={emailEnabled}
                            onChange={(_, checked) => {
                              setEnableEmailSetting(checked)
                            }}
                            color="default"
                        />
                    }
                />
            </TableCell>
        </StyledRow>
  )
}

const phoneNumberValidation = /^\+(\d{10,14})$/

function SmsSettings(): ReactElement {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const smsRecipientToAdd = useAppSelector<string>((state: RootState) => state.settings.smsRecipientToAdd)
  const alarmSettings = useAppSelector<AlarmSettings>((state: RootState) => state.settings.alarmSettings)
  const alarmSettingsCompanyId = useAppSelector<string | null>((state: RootState) => state.settings.alarmSettingsCustomer)

  const [smsEnabled, setSmsEnabled] = useState<boolean>(false)
  const [smsRecipient, setSmsRecipient] = useState<string>()

  useEffect(() => {
    setSmsEnabled(alarmSettings.smsEnabled)
  }, [alarmSettings])

  const invalidSMSAlertRecipient = smsRecipient != null && smsRecipient !== '' && !phoneNumberValidation.test(smsRecipient)

  const handleSMSSubmit = (e: React.KeyboardEvent): void => {
    if (invalidSMSAlertRecipient) {
      return
    }

    if (e.code === 'Enter') { // User presses Enter. Works with mobile as well
      if (smsRecipientToAdd !== '') {
        dispatch(AddSMSRecipientAction(smsRecipientToAdd))
        dispatch(UpdateSMSRecipientTextBoxAction(''))
      }
    }
  }

  const handleUpdateSMSRecipientTextBoxChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    dispatch(UpdateSMSRecipientTextBoxAction(e.target.value))
    setSmsRecipient(e.target.value)
  }

  const addSMSRecipientClicked = (): void => {
    if (smsRecipientToAdd !== '') {
      dispatch(AddSMSRecipientAction(smsRecipientToAdd))
      dispatch(UpdateSMSRecipientTextBoxAction(''))
    }
  }

  const setEnableSMSSetting = (enabled: boolean): void => {
    setSmsEnabled(enabled)
    dispatch(SetSMSEnabledAction({ enabled }))
  }

  return (

        <StyledRow>
            <StyledHeaderCol gray={false}>
                <p>{t('settings_o.sms')}</p>
            </StyledHeaderCol>
            <TableCell>
                <StyledTextField
                    error={invalidSMSAlertRecipient}
                    helperText={invalidSMSAlertRecipient ? t('settings_o.wrong_phone_number_format') : ''}
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    id={'sms_recipient_field'}
                    inputProps={{ 'data-testid': 'sms_recipient_field' }}
                    value={smsRecipientToAdd}
                    disabled={alarmSettingsCompanyId == null || !smsEnabled}
                    onChange={handleUpdateSMSRecipientTextBoxChange}
                    onKeyUp={handleSMSSubmit}
                />
            </TableCell>
            <TableCell>
                <Button
                    style={{ marginLeft: '-0.5em', marginRight: '.6em', padding: '.43em', width: '50%' }}
                    color="primary"
                    variant={'contained'}
                    onClick={addSMSRecipientClicked}
                    data-testid='add-recipient-btn'
                    disabled={smsRecipientToAdd === '' || invalidSMSAlertRecipient || !smsEnabled}
                >
                    OK
                </Button>
                <SmsRecipientChips/>
            </TableCell>
            <TableCell>
                <StyledSelect
                    disableUnderline
                    input={<Input id="selectSMSInverval"/>}
                    value={alarmSettings.smsInterval}
                    disabled={alarmSettingsCompanyId == null || !smsEnabled}
                    onChange={(e) =>
                      dispatch(ChangeSMSIntervalAction(parseInt(e.target.value as string)))}
                    IconComponent={({ className }) => <ExpandMoreIcon className={className}/>}
                >
                    <MenuItem value={1}>
                        {t('settings_o.daily')}
                    </MenuItem>
                    <MenuItem value={2}>
                        {t('settings_o.weekly')}
                    </MenuItem>
                </StyledSelect>
            </TableCell>
            <TableCell>
                <FormControlLabel
                    data-testid='sms-alert-enabled'
                    label={alarmSettings.smsEnabled ? t('settings_o.enabled') : t('settings_o.disabled')}
                    control={
                        <Checkbox
                            disabled={alarmSettingsCompanyId == null}
                            checked={smsEnabled}
                            onChange={(_, checked) => {
                              setEnableSMSSetting(checked)
                            }}
                            color="default"
                        />
                    }
                />
            </TableCell>
        </StyledRow>
  )
}

function TemperatureAlarmSettings(): ReactElement {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const alarmSettings = useAppSelector<AlarmSettings>((state: RootState) => state.settings.alarmSettings)
  const alarmSettingsCompanyId = useAppSelector<string | null>((state: RootState) => state.settings.alarmSettingsCustomer)

  const [temperatureAlertEnabled, setTemperatureAlertEnabled] = useState<boolean>(false)

  useEffect(() => {
    setTemperatureAlertEnabled(alarmSettings.temperatureAlertsEnabled)
  }, [alarmSettings])

  const setEnableTemperatureAlertSetting = (enabled: boolean): void => {
    setTemperatureAlertEnabled(enabled)
    dispatch(SetTemperatureAlertsEnabledAction({ enabled }))
  }

  const invalidAlarmLimits = alarmSettings.temperatureAlertLowerLimit > alarmSettings.temperatureAlertUpperLimit

  return (
        <>
            <StyledRow>
                <StyledHeaderCol gray={false}><h4>{t('settings_o.temperature_alarm')}</h4></StyledHeaderCol>
                <StyledHeaderCol gray={true}><p>{t('settings_o.lower_limit')}</p></StyledHeaderCol>
                <StyledHeaderCol gray={true}><p>{t('settings_o.upper_limit')}</p></StyledHeaderCol>
                <StyledHeaderCol gray={false}/>
                <StyledHeaderCol gray={false}/>
            </StyledRow>
            <StyledRow>
                <TableCell/>
                <TableCell>
                    <StyledTextField
                        fullWidth
                        margin="dense"
                        variant="outlined"
                        id={'temperature_alarm_lower_limit'}
                        inputProps={{ 'data-testid': 'temperature_alarm_lower_limit' }}
                        type={'number'}
                        value={alarmSettings.temperatureAlertLowerLimit}
                        disabled={alarmSettingsCompanyId == null || !temperatureAlertEnabled}
                        error={invalidAlarmLimits}
                        onChange={(e) => dispatch(UpdateTemperatureAlertLowerLimitAction(parseInt(e.target.value)))}
                    />
                </TableCell>
                <TableCell>
                    <StyledTextField
                        fullWidth
                        margin="dense"
                        variant="outlined"
                        id={'temperature_alarm_upper_limit'}
                        inputProps={{ 'data-testid': 'temperature_alarm_upper_limit' }}
                        type={'number'}
                        value={alarmSettings.temperatureAlertUpperLimit}
                        disabled={alarmSettingsCompanyId == null || !temperatureAlertEnabled}
                        error={invalidAlarmLimits}
                        onChange={(e) => dispatch(UpdateTemperatureAlertUpperLimitAction(parseInt(e.target.value)))}
                    />
                </TableCell>
                <TableCell>
                </TableCell>
                <TableCell>
                    <FormControlLabel
                        data-testid='temperature-alerts-enabled'
                        label={alarmSettings.temperatureAlertsEnabled ? t('settings_o.enabled') : t('settings_o.disabled')}
                        control={
                            <Checkbox
                                disabled={alarmSettingsCompanyId == null}
                                checked={temperatureAlertEnabled}
                                onChange={(_, checked) => {
                                  setEnableTemperatureAlertSetting(checked)
                                }}
                                color="default"
                            />
                        }
                    />
                </TableCell>
            </StyledRow>
        </>
  )
}

function EmailRecipientChips(): ReactElement {
  const dispatch = useAppDispatch()
  const emailRecipients = useAppSelector<string[]>((state: RootState) => state.settings.alarmSettings.emailRecipients)

  const onDeleteRecipient = (recipient: string): void => {
    dispatch(RemoveEmailRecipientAction(recipient))
  }

  const validRecipients = emailRecipients.filter(it => it !== '')

  return (
        <>
            {
                validRecipients.map((recipient) =>
                    <AlarmRecipientChip key={recipient} recipient={recipient} onDeleteRecipient={onDeleteRecipient}/>)
            }
        </>
  )
}

function SmsRecipientChips(): ReactElement {
  const dispatch = useAppDispatch()
  const smsRecipients = useAppSelector<string[]>((state: RootState) => state.settings.alarmSettings.smsRecipients)

  const onDeleteRecipient = (recipient: string): void => {
    dispatch(RemoveSMSRecipientAction(recipient))
  }

  const validRecipients = smsRecipients.filter(it => it !== '')
  return (
        <>
            {
                validRecipients.map((recipient) =>
                    <AlarmRecipientChip key={recipient} recipient={recipient} onDeleteRecipient={onDeleteRecipient}/>)
            }
        </>
  )
}

function AlarmRecipientChip(props: Readonly<{
  recipient: string
  onDeleteRecipient: (recipient: string) => void
}>): ReactElement {
  const { recipient, onDeleteRecipient } = props
  return (
        <StyledChip
            label={recipient}
            onDelete={() => {
              onDeleteRecipient(recipient)
            }}
            deleteIcon={<ClearIcon style={{ color: 'white' }}/>}
        />
  )
}
